/*
 * Interface principal das funcionalidades de assinatura sigilosa com o restante do código.
 * As funções nesse arquivo são importadas no `ajax.js`, onde se encontra a lógica responsável pela manipulação da página
 * antes e depois da assinatura/verificação.
 * */

import { pool } from "workerpool";
import { _debugOnConsole } from "./utils";
import { PerformanceResults } from "./worker-interface";
import { WorkerExecutor } from "./worker";

const DEFAULT_SIGNATURE_RESERVED_BYTES = 16000;

/**
 * Função responsável pela assinatura sigilosa, sem enviar o Documento ao back-end.
 * @param file_list Os PDFs a serem assinados.
 * @param token Token para re-captcha.
 * @param rsa_key_size Tamanho da chave RSA usada para assinar o PDF.
 * @returns os PDFs assinados
 */
export async function private_sign(fileList: FileList, token: string | undefined, rsa_key_size: number): Promise<any> {
  //const workerpool = pool(workerUrl.toString(), {
  //  minWorkers: 1,
  //  workerOpts: { type: "module" },
  //});
  const workerProxy = new WorkerExecutor();

  _debugOnConsole("Assinando %s PDFs...", fileList.length);

  let promises: Array<Promise<any>> = [];
  let numSignedPdfs = 0;

  const signingTime = await fetch("/api/v1/server-time")
    .then((res) => res.json())
    .then((info) => 1800 + 1000 * info.server_time);

  let perf: PerformanceResults = {
    placeholder: {
      name: "Criação do Placeholder",
      value: -1,
    },
    hash: {
      name: "Cálculo do hash do PDF",
      value: -1,
    },
    csr: {
      name: "Criação da CSR",
      value: -1,
    },
    server: {
      name: "Assinatura do certificado (afetado pela rede)",
      value: -1,
    },
    key: {
      name: `Criação da chave privada (RSA ${rsa_key_size})`,
      value: -1,
    },
    signature: {
      name: "Realização da Assinatura",
      value: -1,
    },
    total: {
      name: "Tempo total",
      value: performance.now(),
    },
  };

  // In practive, we only accept one file at a time, because it's easier.
  // This code would work with multiple files, but I don't have enough time to code the interface and performance measurements.
  for (let i = 0; i < 1; ++i) {
    promises.push(
      fileList[i].arrayBuffer().then(async (buffer) => {
        const doSign = async (signatureReservedBytes: number) => {
          const { perf_results, pdfWithPlaceholder, pdfHashValue, csrPem, privatePemRsaKey } = await workerProxy.prepareSign(
            // @ts-ignore
            Buffer.from(buffer),
            "SHA-256",
            signatureReservedBytes,
            signingTime,
            rsa_key_size,
            perf
          );
          perf = perf_results;

          _debugOnConsole("Enviando requisição para obter o PKCS#7...");

          const form = new FormData();
          console.log(typeof csrPem);
          console.log(typeof pdfHashValue);
          form.append("csr_string", csrPem);
          form.append("hash_digest", pdfHashValue);
          form.append("hash_algorithm", "sha256");

          perf.server.value = performance.now();
          const res = await fetch("/api/v1/issue", {
            method: "POST",
            body: form,
          });
          perf.server.value = performance.now() - perf.server.value;

          _debugOnConsole("Response code: %s", res.status);
          if (!res.ok) throw new Error(`Erro durante a obtenção do PKCS#7. Status code: ${res.status}, resposta: ${res.text()}`);

          const pkcs7 = await res.text();
          const [signed, perf_res, signatue_count] = await workerProxy.sign(
            pdfWithPlaceholder,
            privatePemRsaKey,
            pkcs7,
            "sha-256",
            signatureReservedBytes,
            signingTime,
            perf
          );
          perf = perf_res;

          numSignedPdfs++;
          _debugOnConsole("PDFs assinados: %s/%s", numSignedPdfs, fileList.length);

          const fileNameWithNoExtension = fileList[i].name.slice(0, fileList[i].name.lastIndexOf("."));
          return {
            signedFile: new File([new Blob([signed.buffer], { type: "application/pdf" })], `${fileNameWithNoExtension}_assinado.pdf`, {
              type: "application/pdf",
            }),
            privateKey: privatePemRsaKey,
            pkcs7: pkcs7,
            signature_count: signatue_count,
          };
        };

        return doSign(DEFAULT_SIGNATURE_RESERVED_BYTES).catch((e) => {
          if (e.expectedSize !== undefined) {
            _debugOnConsole("Placeholder muito pequeno! Novo tamanho: %s", e.expectedSize);
            return doSign(e.expectedSize);
          }
          throw e;
        });
      })
    );
  }

  const { signedFile, privateKey, pkcs7, signature_count } = await promises[0];
  perf.total.value = performance.now() - perf.total.value;

  return {
    signedFile,
    privateKey,
    pkcs7,
    perf,
    signature_count,
  };
}
